import { captureMessage } from '@sentry/browser';
import { Severity } from '@sentry/types';
import axios from 'axios';

import NetworkService from '../NetworkService';

const { hasOwnProperty } = Object.prototype;

const logFailedActionOnSentry = (errorMessage) => {
    try {
        captureMessage(JSON.stringify(errorMessage), Severity.Error);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.log('axios-state', { errorMessage });
    }
};

function handleFailedResponse(config, requestIndex, error, store, action) {
    if (axios.isCancel(error)) {
        return store.dispatch({
            ...action,
            type: hasOwnProperty.call(action.actionTypes, 'canceled')
                ? action.actionTypes.canceled
                : action.actionTypes.fail,
            payload: null,
            requestAction: action,
            requestConfig: config,
            requestIndex,
        });
    }

    logFailedActionOnSentry(action);

    return store.dispatch({
        type: action.actionTypes.fail,
        payload: error,
        requestAction: action,
        requestConfig: config,
        requestIndex,
    });
}

const handleWarningResponse = (config, requestIndex, action, nextAction) => {
    if (typeof action?.meta?.onAxiosFinally === 'function') {
        action.meta.onAxiosFinally(nextAction);
    }

    return nextAction;
};

const callRequest = (client, source, action, config, store, requestIndex = 0) => {
    if (action.actionTypes.start) {
        store.dispatch({
            type: action.actionTypes.start,
            requestAction: action,
            requestConfig: config,
            requestIndex,
        });
    }

    client
        .request({
            cancelToken: source.token,
            ...config,
        })
        .then(res => store.dispatch({
            type: action.actionTypes.success,
            payload: res,
            requestAction: action,
            requestConfig: config,
            requestIndex,
        }))
        .catch(error => handleFailedResponse(config, requestIndex, error, store, action))
        .then(res => handleWarningResponse(config, requestIndex, action, res))
        .finally((res) => {
            if (action.actionTypes.finish) {
                store.dispatch({
                    type: action.actionTypes.finish,
                    payload: res,
                    requestAction: action,
                    requestConfig: config,
                    requestIndex,
                });
            }
        });
};

export const createAxiosMiddleware = ({
    suffix = '_REQUEST_CALLED',
} = {}) => store => next => (action) => {
    if (action.type.endsWith(suffix)) {
        const client = NetworkService.getClient(action.type, action.meta?.axios?.clientConfig);

        const source = axios.CancelToken.source();
        const newAction = next({
            ...action,
            meta: {
                ...action.meta,
                axiosCancel: () => source.cancel(),
            },
        });

        const configs = Array.isArray(action.config) ? action.config : [action.config];
        configs.forEach((config, requestIndex) => callRequest(
            client,
            source,
            action,
            config,
            store,
            requestIndex,
        ));

        return newAction;
    }

    return next(action);
};

export const toCancellable = (action) => {
    const { meta } = action || {};
    if (!meta || !meta.axiosCancel) return () => {};
    return () => {
        meta.axiosCancel();
    };
};
